/**
 * SPARE WAY AUTO LINK report — another branch of the Main Branch.
 *
 * Ledger rules:
 *  - Voucher type "Credit Sales"  → Debit  (Main sells to Spare Way)
 *  - Voucher type "Inward Supply" → Credit (party is CR)
 */
import { salesImportBranchDisplayName, normalizeSalesImportBranchId } from "../../salesDataImport/config/salesImportBranchIds";
import { FY_MONTH_ORDER_SHORT } from "../../../utils/financialYear";
import { expenseRowDebitCredit, expenseRowSide } from "./expenseReportService";

export const PARTY_SPARE_WAY_AUTO_LINK = "SPARE WAY AUTO LINK";
export const SPARE_WAY_MAIN_BRANCH_ID = "1";

function isMainBranchRow(row) {
  return normalizeSalesImportBranchId(row.branchId) === SPARE_WAY_MAIN_BRANCH_ID;
}

function normVoucherType(row) {
  return String(row?.voucherTypeName ?? row?.voucherType ?? "").trim();
}

function rowAmount(row) {
  const { debit, credit } = expenseRowDebitCredit(row);
  return Math.max(debit, credit);
}

function isDebitTransactionRow(row) {
  return expenseRowSide(row) === "debit";
}

function isCreditTransactionRow(row) {
  return expenseRowSide(row) === "credit";
}

export function isSpareWayAutoLinkParty(row) {
  const name = String(row?.partyName ?? row?.party ?? row?.customerName ?? "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!name) return false;
  const compact = name.replace(/[\s.\-_/()]/g, "");
  return compact.includes("SPAREWAY") && compact.includes("AUTOLINK");
}

/** Debit: Credit Sales · SPARE WAY AUTO LINK · Main Branch · transaction type Debit */
export function isSpareWayCreditSalesRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  const vt = normVoucherType(row).toUpperCase();
  if (vt.includes("CREDIT NOTE")) return false;
  if (!vt.includes("CREDIT SALE")) return false;
  return isDebitTransactionRow(row);
}

/** Credit: Credit Note · SPARE WAY AUTO LINK · Main Branch · transaction type Credit (CR) */
export function isSpareWayCreditNoteRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  const vt = normVoucherType(row).toUpperCase();
  if (!vt.includes("CREDIT NOTE")) return false;
  return isCreditTransactionRow(row);
}

/** Credit: Inward Supply · SPARE WAY AUTO LINK · Main Branch · transaction type Credit (CR) */
export function isSpareWayInwardSupplyRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  const vt = normVoucherType(row).toUpperCase();
  if (vt.includes("DEBIT NOTE")) return false;
  if (!vt.includes("INWARD SUPPLY")) return false;
  return isCreditTransactionRow(row);
}

/** Debit Note reduces Inward Supply (Cr) · SPARE WAY AUTO LINK · Main Branch · transaction type Debit */
export function isSpareWayDebitNoteRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  const vt = normVoucherType(row).toUpperCase();
  if (!vt.includes("DEBIT NOTE")) return false;
  return isDebitTransactionRow(row);
}

function inwardSupplyNet(gross, debitNote) {
  return Math.max(0, (gross || 0) - (debitNote || 0));
}

function typeBlob(row) {
  const txn = String(row?.transactionType ?? "").trim();
  return `${normVoucherType(row)} ${txn}`.toUpperCase();
}

/** Credit: Receipt · SPARE WAY AUTO LINK · Main Branch · transaction type Credit */
export function isSpareWayReceiptRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  const blob = typeBlob(row);
  if (!blob.includes("RECEIPT")) return false;
  if (blob.includes("JOURNAL")) return false;
  return isCreditTransactionRow(row);
}

function isJournalVoucherRow(row) {
  const blob = typeBlob(row);
  if (blob.includes("RECEIPT")) return false;
  return blob.includes("JOURNAL");
}

/** Credit: Journal · SPARE WAY AUTO LINK · Main Branch · transaction type Credit */
export function isSpareWayJournalCreditRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  if (!isJournalVoucherRow(row)) return false;
  return isCreditTransactionRow(row);
}

/** Debit: Payment · SPARE WAY AUTO LINK · Main Branch · transaction type Debit */
export function isSpareWayPaymentRow(row) {
  if (!isMainBranchRow(row)) return false;
  if (!isSpareWayAutoLinkParty(row)) return false;
  const blob = typeBlob(row);
  if (!blob.includes("PAYMENT")) return false;
  if (blob.includes("RECEIPT") || blob.includes("JOURNAL")) return false;
  return isDebitTransactionRow(row);
}

function enrichLine(row) {
  const { debit, credit } = expenseRowDebitCredit(row);
  return {
    ...row,
    amountAbs: Math.max(debit, credit),
    debit,
    credit,
    branchLabel: salesImportBranchDisplayName(row.branchId),
    voucherTypeName: normVoucherType(row) || row.voucherTypeName,
    partyName: String(row.partyName || "").trim(),
  };
}

function sortLines(a, b) {
  return (
    String(a.tranDate).localeCompare(String(b.tranDate)) ||
    String(a.voucherNo).localeCompare(String(b.voucherNo))
  );
}

/** Voucher lines + totals for one calendar month. */
export function buildSpareWayMonthDetail(rows, { calendarYear, monthIndex }) {
  const lines = [];
  let creditSalesTotal = 0;
  let inwardSupplyTotal = 0;
  let receiptTotal = 0;
  let journalCreditTotal = 0;
  let creditNoteTotal = 0;
  let debitNoteTotal = 0;
  let paymentTotal = 0;

  for (const row of rows || []) {
    if (row.calendarYear !== calendarYear || row.monthIndex !== monthIndex) continue;
    if (isSpareWayCreditSalesRow(row)) {
      const line = enrichLine(row);
      creditSalesTotal += line.debit;
      lines.push(line);
    } else if (isSpareWayCreditNoteRow(row)) {
      const line = enrichLine(row);
      creditNoteTotal += line.credit;
      lines.push(line);
    } else if (isSpareWayInwardSupplyRow(row)) {
      const line = enrichLine(row);
      inwardSupplyTotal += line.credit;
      lines.push(line);
    } else if (isSpareWayDebitNoteRow(row)) {
      const line = enrichLine(row);
      debitNoteTotal += line.debit;
      lines.push(line);
    } else if (isSpareWayReceiptRow(row)) {
      const line = enrichLine(row);
      receiptTotal += line.credit;
      lines.push(line);
    } else if (isSpareWayJournalCreditRow(row)) {
      const line = enrichLine(row);
      journalCreditTotal += line.credit;
      lines.push(line);
    } else if (isSpareWayPaymentRow(row)) {
      const line = enrichLine(row);
      paymentTotal += line.debit;
      lines.push(line);
    }
  }

  lines.sort(sortLines);

  const inwardSupplyGross = inwardSupplyTotal;
  const inwardSupplyNetTotal = inwardSupplyNet(inwardSupplyGross, debitNoteTotal);
  const debitTotal = creditSalesTotal + paymentTotal;
  const receiptCombinedTotal = receiptTotal + journalCreditTotal;
  const creditTotal = inwardSupplyNetTotal + creditNoteTotal + receiptCombinedTotal;

  return {
    lines,
    summary: {
      creditSalesTotal,
      creditNoteTotal,
      inwardSupplyGross,
      debitNoteTotal,
      inwardSupplyTotal: inwardSupplyNetTotal,
      receiptTotal,
      journalCreditTotal,
      receiptCombinedTotal,
      paymentTotal,
      debitTotal,
      creditTotal,
      netTotal: debitTotal - creditTotal,
      lineCount: lines.length,
    },
  };
}

/** FY month-wise summary: Credit Sales + Payment (Dr), Inward Supply + Receipt (Cr), Net. */
export function buildSpareWayFySummary(rows) {
  const bySlot = new Map();

  for (const row of rows || []) {
    const slot = Number(row.fySlot);
    if (!Number.isFinite(slot)) continue;

    let bucket = null;
    if (isSpareWayCreditSalesRow(row)) bucket = "creditSales";
    else if (isSpareWayCreditNoteRow(row)) bucket = "creditNote";
    else if (isSpareWayInwardSupplyRow(row)) bucket = "inwardSupply";
    else if (isSpareWayDebitNoteRow(row)) bucket = "debitNote";
    else if (isSpareWayReceiptRow(row)) bucket = "receipt";
    else if (isSpareWayJournalCreditRow(row)) bucket = "journalCredit";
    else if (isSpareWayPaymentRow(row)) bucket = "payment";
    if (!bucket) continue;

    const curr = bySlot.get(slot) || {
      fySlot: slot,
      fyMonthLabel: row.fyMonthLabel || FY_MONTH_ORDER_SHORT[slot] || "",
      calendarYear: row.calendarYear,
      monthIndex: row.monthIndex,
      creditSales: 0,
      creditNote: 0,
      inwardSupply: 0,
      debitNote: 0,
      receipt: 0,
      journalCredit: 0,
      payment: 0,
      net: 0,
    };

    curr[bucket] += rowAmount(row);
    bySlot.set(slot, curr);
  }

  const months = [...bySlot.values()]
    .sort((a, b) => a.fySlot - b.fySlot)
    .map((m) => {
      const inwardSupplyNetAmt = inwardSupplyNet(m.inwardSupply, m.debitNote);
      return {
        ...m,
        inwardSupplyNet: inwardSupplyNetAmt,
        receiptCombined: m.receipt + m.journalCredit,
        net:
          m.creditSales +
          m.payment -
          inwardSupplyNetAmt -
          m.creditNote -
          m.receipt -
          m.journalCredit,
      };
    });

  const totals = months.reduce(
    (acc, m) => ({
      creditSales: acc.creditSales + m.creditSales,
      creditNote: acc.creditNote + m.creditNote,
      inwardSupply: acc.inwardSupply + m.inwardSupply,
      debitNote: acc.debitNote + m.debitNote,
      inwardSupplyNet: acc.inwardSupplyNet + m.inwardSupplyNet,
      receipt: acc.receipt + m.receipt,
      journalCredit: acc.journalCredit + m.journalCredit,
      receiptCombined: acc.receiptCombined + m.receiptCombined,
      payment: acc.payment + m.payment,
      net: acc.net + m.net,
    }),
    {
      creditSales: 0,
      creditNote: 0,
      inwardSupply: 0,
      debitNote: 0,
      inwardSupplyNet: 0,
      receipt: 0,
      journalCredit: 0,
      receiptCombined: 0,
      payment: 0,
      net: 0,
    }
  );

  return { months, totals };
}

export const SPARE_WAY_RULES = [
  {
    id: "credit-sales",
    title: "Credit Sales (Dr)",
    voucherType: "Credit Sales",
    party: PARTY_SPARE_WAY_AUTO_LINK,
    branch: "Main Branch (Branch ID 1) · Transaction type Debit (Dr)",
  },
  {
    id: "credit-note",
    title: "Credit Note (Cr)",
    voucherType: "Credit Note",
    party: PARTY_SPARE_WAY_AUTO_LINK,
    branch: "Main Branch (Branch ID 1) · Transaction type Credit (CR)",
  },
  {
    id: "inward-supply",
    title: "Inward Supply (Cr)",
    voucherType: "Inward Supply − Debit Note",
    party: PARTY_SPARE_WAY_AUTO_LINK,
    branch: "Main Branch (ID 1) · Inward Supply (Cr) less Debit Note (Dr)",
  },
  {
    id: "debit-note",
    title: "Debit Note (less Inward Supply)",
    voucherType: "Debit Note",
    party: PARTY_SPARE_WAY_AUTO_LINK,
    branch: "Main Branch (Branch ID 1) · Transaction type Debit (Dr)",
  },
  {
    id: "receipt",
    title: "Receipt (Cr) + Journal credit",
    voucherType: "Receipt + Journal",
    party: PARTY_SPARE_WAY_AUTO_LINK,
    branch: "Main Branch (Branch ID 1) · Transaction type Credit (CR)",
  },
  {
    id: "payment",
    title: "Payment (Dr)",
    voucherType: "Payment",
    party: PARTY_SPARE_WAY_AUTO_LINK,
    branch: "Main Branch (Branch ID 1) · Transaction type Debit (Dr)",
  },
];
